import { useEffect, useRef } from 'react'

/**
 * Cross-platform keyboard shortcut hook.
 *
 *   useHotkey('ctrl+k', () => open(), { enabled: true })
 *   useHotkey('escape', closeModal, { enabled: open })
 *   useHotkey('cmd+s', save) // Cmd on Mac, Ctrl elsewhere if `mod` keyword used
 *   useHotkey('mod+k', () => ...) // mod = Cmd on Mac, Ctrl elsewhere
 *
 * Combo format: tokens joined with '+'. Recognised modifiers:
 *   ctrl, control, cmd, meta, alt, option, shift, mod (Cmd on Mac else Ctrl)
 * Non-modifier token is the key (case-insensitive). 'esc' aliases 'escape'.
 */
const isMac = () =>
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || '')

const parseCombo = (combo) => {
  const parts = String(combo || '')
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean)

  const mods = { ctrl: false, meta: false, alt: false, shift: false }
  let key = ''
  parts.forEach((p) => {
    if (p === 'ctrl' || p === 'control') mods.ctrl = true
    else if (p === 'cmd' || p === 'meta') mods.meta = true
    else if (p === 'alt' || p === 'option') mods.alt = true
    else if (p === 'shift') mods.shift = true
    else if (p === 'mod') {
      if (isMac()) mods.meta = true
      else mods.ctrl = true
    } else {
      key = p === 'esc' ? 'escape' : p
    }
  })
  return { mods, key }
}

const matches = (e, combo) => {
  const { mods, key } = parseCombo(combo)
  if (!key) return false
  const ev = String(e.key || '').toLowerCase()
  if (ev !== key) return false
  if (Boolean(e.ctrlKey) !== mods.ctrl) return false
  if (Boolean(e.metaKey) !== mods.meta) return false
  if (Boolean(e.altKey) !== mods.alt) return false
  if (Boolean(e.shiftKey) !== mods.shift) return false
  return true
}

const isEditableTarget = (target) => {
  if (!target) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return false
}

const useHotkey = (combo, handler, options = {}) => {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const { enabled = true, allowInInputs = false, preventDefault = true } = options

  useEffect(() => {
    if (!enabled) return undefined
    const onKey = (e) => {
      if (!allowInInputs && isEditableTarget(e.target)) {
        // Allow ctrl/meta/alt-modified shortcuts even in inputs (e.g. ctrl+s).
        const hasMod = e.ctrlKey || e.metaKey || e.altKey
        if (!hasMod && parseCombo(combo).key !== 'escape') return
      }
      const combos = Array.isArray(combo) ? combo : [combo]
      if (combos.some((c) => matches(e, c))) {
        if (preventDefault) e.preventDefault()
        handlerRef.current?.(e)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [combo, enabled, allowInInputs, preventDefault])
}

export default useHotkey
